import React from 'react';
import { colors } from '../../../../colors';
import type {
  CPCPhase,
  CPCQueueStateMessage,
} from '../../../../bridge/utils/CPCLiveMessages';
import type { BestCalculationResult } from 'grts-utils/types/ProfitCalcs';
import './shared.css';

export const PHASE_LABEL: Record<CPCPhase, string> = {
  LOOP_1_CPC: 'Loop 1 — CPC',
  LOOP_1B_RSI_MRC: 'Loop 1 — MRC',
  LOOP_1C_IIC: 'Loop 1 — IIC',
  LOOP_2_CPC: 'Loop 2 — CPC',
  LOOP_2_IIC: 'Loop 2 — IIC',
};

export const formatDurationCompact = (ms?: number | null): string => {
  if (!ms) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

export const formatScore = (n: number, digits = 4): string => n.toFixed(digits);
export const formatBalance = (n: number): string =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const formatNumber = (
  n: number | undefined | null,
  digits = 2,
): string =>
  n == null
    ? '—'
    : n.toLocaleString(undefined, { maximumFractionDigits: digits });

export const Bar = ({
  pct,
  label,
  height = 12,
  color = colors.green,
}: {
  pct: number;
  label?: string;
  height?: number;
  color?: string;
}) => {
  const clampedPct = Math.min(100, Math.max(0, pct));
  const isComplete = clampedPct >= 99.9;
  const fillClasses = ['live-cpc-bar-fill', isComplete ? 'is-complete' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div
      className="live-cpc-bar-wrap"
      style={{ ['--bar-color' as string]: color } as React.CSSProperties}
    >
      {label && (
        <div className="live-cpc-bar-label">
          <span className="live-cpc-bar-label-text">{label}</span>
          <span className="live-cpc-bar-label-pct">
            {clampedPct.toFixed(1)}%
          </span>
        </div>
      )}
      <div
        className="live-cpc-bar-track"
        style={{ height, borderRadius: height / 2 }}
      >
        <div
          className={fillClasses}
          style={{ width: `${clampedPct}%`, borderRadius: height / 2 }}
        />
      </div>
    </div>
  );
};

export const InterruptedBanner = ({ reason }: { reason: string }) => (
  <div
    style={{
      padding: '8px 12px',
      borderRadius: 6,
      background: 'rgba(240, 22, 47, 0.15)',
      border: `1px solid ${colors.red}`,
      color: colors.lightRed,
      fontSize: 13,
    }}
  >
    Run interrupted: {reason}
  </div>
);

type QueueItem = CPCQueueStateMessage['queue'][number];

export const QueueListItem = ({
  item,
  variant = 'list',
  onClick,
  rightLabel,
}: {
  item: QueueItem;
  variant?: 'list' | 'chip' | 'card';
  onClick?: (() => void) | undefined;
  rightLabel?: string;
}) => {
  if (variant === 'chip') {
    return (
      <div
        style={{
          padding: '6px 10px',
          borderRadius: 14,
          background: item.active ? colors.darkGreen : 'rgba(255,255,255,0.05)',
          color: colors.white,
          fontSize: 13,
          whiteSpace: 'nowrap',
        }}
      >
        {item.active ? '▶ ' : ''}
        {item.botFriendlyName}
      </div>
    );
  }
  if (variant === 'card') {
    return (
      <div
        style={{
          padding: 10,
          borderRadius: 8,
          background: item.active ? colors.darkGreen : 'rgba(255,255,255,0.04)',
          color: colors.white,
          minWidth: 130,
          border: item.active
            ? `1px solid ${colors.green}`
            : '1px solid transparent',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          {item.active ? '▶ ' : ''}
          {item.botFriendlyName}
        </div>
        <div style={{ fontSize: 11, color: colors.gray, marginTop: 2 }}>
          last {formatDurationCompact(item.durationCPC)}
        </div>
      </div>
    );
  }
  const isClickable = !!onClick;
  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={isClickable ? 'live-cpc-queue-item-clickable' : undefined}
      style={{
        padding: '8px 12px',
        borderRadius: 6,
        background: item.active ? colors.darkGreen : 'transparent',
        color: colors.white,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 3,
        cursor: isClickable ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <span style={{ fontSize: 15, fontWeight: item.active ? 600 : 400 }}>
        {item.active ? '▶ ' : '  '}
        {item.botFriendlyName}
      </span>
      {rightLabel && (
        <span
          style={{
            opacity: 0.8,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {rightLabel}
        </span>
      )}
    </div>
  );
};

const BestKV = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      padding: '4px 0',
    }}
  >
    <span style={{ color: colors.gray, fontSize: 14 }}>{label}</span>
    <span
      style={{
        color: colors.white,
        fontSize: 14,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </span>
  </div>
);

const formatIndicatorConfig = (
  ic: BestCalculationResult['indicatorConfig'],
): string => {
  if (!ic) return 'none';
  const oppo = `opp ${ic.oppositeOrderTimeFrame}/${ic.oppositeOrderChildTimeFrame} p${ic.oppositeOrderPeriod} t${ic.oppositeOrderThreshold}`;
  const same = `same ${ic.sameOrderTimeFrame}/${ic.sameOrderChildTimeFrame} p${ic.sameOrderPeriod} t${ic.sameOrderThreshold}`;
  return `${oppo} · ${same}`;
};

const formatMrcRsiConfig = (
  mrc: BestCalculationResult['mrcRsiConfig'],
): string => {
  if (!mrc) return 'none';
  return `${mrc.rsiType} h${mrc.higherTRsiPeriod}/s${mrc.shorterTRsiPeriod}/m${mrc.multiTRsiPeriod} t${mrc.rsiThreshold} (${mrc.resetMode})`;
};

const formatPercent = (n: number, digits = 2): string => {
  const sign = n > 0 ? '+' : '';
  return `${sign}${(n * 100).toFixed(digits)}%`;
};

const HeadlineMetric = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <div
      style={{
        fontSize: 26,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color: color ?? colors.white,
        lineHeight: 1.1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: 12,
        color: colors.gray,
        textTransform: 'uppercase',
        letterSpacing: 1,
      }}
    >
      {label}
    </div>
  </div>
);

export const GlobalBestCard = ({
  result,
}: {
  result: BestCalculationResult | null;
}) => {
  if (!result) {
    return (
      <div style={{ color: colors.gray, fontSize: 14 }}>
        No best result yet.
      </div>
    );
  }
  const tag = `${result.params.higherTimeFrame} · ${result.params.shorterTimeFrame} · ${result.params.multiOrderTimeFrame}`;
  const profitColor = result.highestProfit >= 0 ? colors.green : colors.red;
  return (
    <div style={{ color: colors.white }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 18,
          marginBottom: 16,
        }}
      >
        <HeadlineMetric label="Score" value={formatScore(result.score)} />
        <HeadlineMetric
          label="Base score"
          value={formatScore(result.baseScore)}
        />
        <HeadlineMetric
          label="Total balance"
          value={formatBalance(result.totalBalanceValue)}
        />
        <HeadlineMetric
          label="Stable profit"
          value={formatPercent(result.highestProfit)}
          color={profitColor}
        />
      </div>
      {result.highestProfitInAsset != null && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            padding: '4px 0',
            marginBottom: 8,
          }}
        >
          <span style={{ color: colors.gray, fontSize: 14 }}>Asset profit</span>
          <span
            style={{
              color:
                result.highestProfitInAsset >= 0 ? colors.green : colors.red,
              fontSize: 16,
              fontWeight: 600,
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatPercent(result.highestProfitInAsset)}
          </span>
        </div>
      )}
      <BestKV label="TimeFrames" value={tag} />
      <BestKV label="cbrt" value={formatNumber(result.params.cbrt, 4)} />
      <BestKV
        label="stfi"
        value={String(result.params.shorterTimeFrameInterval)}
      />
      <BestKV
        label="cbrtRange"
        value={result.params.supportCbrtRange ? 'on' : 'off'}
      />
      <BestKV
        label="MRC state"
        value={result.params.supportMrcState ? 'on' : 'off'}
      />
      <BestKV label="RSI-MRC" value={formatMrcRsiConfig(result.mrcRsiConfig)} />
      <BestKV
        label="Indicator config"
        value={formatIndicatorConfig(result.indicatorConfig)}
      />
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 8,
      padding: '4px 0',
    }}
  >
    <span style={{ color: colors.gray, fontSize: 14 }}>{label}</span>
    <span
      style={{
        color: colors.white,
        fontSize: 15,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </span>
  </div>
);

export type StatsInput = {
  baseAsset: string | null;
  phase: CPCPhase | null;
  phasePass: number | null;
  phaseDone: number;
  phaseTotal: number;
  comboIndex: number;
  comboTotal: number;
  currentCombo: {
    higherTimeFrame: string;
    shorterTimeFrame: string;
    multiOrderTimeFrame: string;
  } | null;
  comboETA: number | null;
  phaseDurationMs: number | null;
  totalDurationMs: number | null;
};

const phaseValue = (phase: CPCPhase | null, pass: number | null): string => {
  if (!phase) return '—';
  if (phase === 'LOOP_2_CPC' || phase === 'LOOP_2_IIC') {
    const subPhase = phase === 'LOOP_2_CPC' ? 'CPC' : 'IIC';
    const passNum = pass != null && pass > 0 ? pass : 1;
    return `Loop 2.${passNum} — ${subPhase}`;
  }
  return PHASE_LABEL[phase];
};

export const StatsBlock = ({ input }: { input: StatsInput }) => (
  <div style={{ color: colors.white, display: 'grid', gap: 6 }}>
    <Row
      label="Bot"
      value={input.baseAsset ? `${input.baseAsset}-USDC` : '—'}
    />
    <Row label="Phase" value={phaseValue(input.phase, input.phasePass)} />
    <Row
      label="Iterations"
      value={`${input.phaseDone} / ${input.phaseTotal || '—'}`}
    />
    <Row
      label="Combo"
      value={`${input.comboIndex + 1} of ${input.comboTotal || '—'}`}
    />
    <Row
      label="Current combo TF"
      value={
        input.currentCombo
          ? `${input.currentCombo.higherTimeFrame} · ${input.currentCombo.shorterTimeFrame} · ${input.currentCombo.multiOrderTimeFrame}`
          : '—'
      }
    />
    <Row label="Combo ETA" value={formatDurationCompact(input.comboETA ?? 0)} />
    <Row
      label="Phase duration"
      value={formatDurationCompact(input.phaseDurationMs)}
    />
    <Row
      label="Total CPC duration"
      value={formatDurationCompact(input.totalDurationMs)}
    />
  </div>
);

export const Panel = ({
  title,
  children,
  style,
}: {
  title?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: 'rgba(255,255,255,0.04)',
      borderRadius: 10,
      padding: 18,
      ...style,
    }}
  >
    {title && (
      <div
        style={{
          color: colors.gray,
          fontSize: 14,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
          marginBottom: 14,
          fontWeight: 600,
        }}
      >
        {title}
      </div>
    )}
    {children}
  </div>
);
